import useId from '@hooks/use-id';
import { logger } from '@modules/logger';
import { createContext, useCallback, useContext } from 'react';

import styles from './FormSelector.module.scss';

interface FormSelectorContextValues {
  onSelect: (newValue: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedValues: string[];
  size: 'sm' | 'md' | 'lg';
}

export const FormSelectorContext = createContext<FormSelectorContextValues>({
  onSelect: () => logger.warn('onSelect was not provided for FormSelectorContext'),
  selectedValues: [],
  size: 'lg',
});

export type FormSelectorGroupProps = {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
} & (
  | {
      exclusive?: true;
      selectedValue: string;
      onChange: (newValue: string, event: React.ChangeEvent<HTMLInputElement>) => void;
    }
  | {
      exclusive: false;
      selectedValue: string[];
      onChange: (newValues: string[], event: React.ChangeEvent<HTMLInputElement>) => void;
    }
);

const FormSelectorGroup: React.FC<React.PropsWithChildren<FormSelectorGroupProps>> = (props) => {
  const handleSelect = useCallback(
    (selectedValue: string, event: React.ChangeEvent<HTMLInputElement>) => {
      if (props.exclusive === true || props.exclusive === undefined) {
        props.onChange(selectedValue, event);
      } else if (props.exclusive === false) {
        props.onChange([...props.selectedValue, selectedValue], event);
      }
    },
    [props],
  );

  const selectedValues = Array.isArray(props.selectedValue) ? props.selectedValue : [props.selectedValue];
  return (
    <FormSelectorContext.Provider
      value={{ selectedValues, onSelect: handleSelect, size: props.size || 'lg' }}
    >
      <div className={props.className} role="group">
        {props.children}
      </div>
    </FormSelectorContext.Provider>
  );
};

interface FormSelectorProps {
  value: string;
  disabled?: boolean;
  className?: string;
  withHoverStyle?: boolean;
}

const FormSelector: React.FC<React.PropsWithChildren<FormSelectorProps>> = (props) => {
  const { onSelect, selectedValues, size } = useContext(FormSelectorContext);
  const isSelected = selectedValues.includes(props.value);
  const id = useId();
  const className = props.className
    ? `${styles['form-selector-label']} ${props.className}`
    : styles['form-selector-label'];

  return (
    <div
      data-is-with-hover-style={props.withHoverStyle}
      className={styles['form-selector']}
      data-disabled={props.disabled}
      data-size={size}
    >
      <input
        type="radio"
        className={styles['form-selector-input']}
        aria-labelledby={id}
        checked={isSelected}
        disabled={props.disabled}
        value={props.value}
        onChange={(event) => onSelect(props.value, event)}
      />

      <label data-is-checked={isSelected} htmlFor={id} className={className}>
        {props.children}
      </label>
    </div>
  );
};

export { FormSelectorGroup, FormSelector };
